import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import { LECTURE_ROUTES } from "./lectureRoutes";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import QBankFilter from "../../questionBank/QBankFilter";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../../questionBank/QuestionBankList.css";
import { BackSVG, LanguageMobileIcon } from "../../components/SvgIcons";
import bookmark from "../../assets/images/bookmark.png";
import { useSidebarData } from "../../hooks/useSidebarData";
import { useLecturesList } from "../../hooks/useLecturesList";
import { useLecturesListActions } from "../../hooks/useLecturesFeedback";
import {
  resolveQuestionBankStateId,
  resolveQuestionBankStateName,
} from "../../questionBank/stateUtils";
import {
  buildLectureFlowState,
  getStoredLectureFlowState,
  persistLectureFlowState,
} from "./lectureFlowState";
import LectureFeedbackModal from "./LectureFeedbackModal";

const LECTURES_CATEGORY_ID = 3;
const DEFAULT_SUBJECT_ID = 1;
const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
const mapFilters = (filters = {}) =>
  Object.entries(filters).reduce((result, [key, value]) => {
    if (value !== undefined && value !== null && value !== "")
      result[key === "tags" ? "lectures_tags" : key] = value;
    return result;
  }, {});

function ListShimmer() {
  return (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <article className="qbl-questionCard" key={index}>
          <div className="qbl-questionCard__head">
            <span className="dashboard-skeleton dashboard-skeleton--button" />
            <span className="dashboard-skeleton dashboard-skeleton--icon" />
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--line" />
          <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--text-short" />
          <div className="qbl-questionCard__footer">
            {Array.from({ length: 4 }).map((__, itemIndex) => (
              <span
                className="dashboard-skeleton dashboard-skeleton--line qbl-questionCard__shimmer-footer"
                key={itemIndex}
              />
            ))}
          </div>
        </article>
      ))}
    </>
  );
}

function LectureCard({
  lecture,
  onOpen,
  onBookmark,
  onLike,
  onFeedback,
  bookmarkPending,
  likePending,
}) {
  return (
    <article
      className={`qbl-questionCard${lecture.isBold ? " qbl-questionCard--active" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(lecture)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(lecture);
        }
      }}
    >
      <div className="qbl-questionCard__head">
        <div className="qbl-questionCard__meta">
          <span className="qbl-questionCard__number">
            {String(lecture.serialNo).padStart(2, "0")}
          </span>
          <span className="qbl-questionCard__lock">
            <i
              className={`bi ${lecture.paymentStatus === 1 ? "bi-unlock-fill" : "bi-lock-fill"}`}
            />
          </span>
        </div>
        <button
          type="button"
          className="qbl-questionCard__bookmark"
          aria-label="Bookmark lecture"
          disabled={bookmarkPending}
          onClick={(event) => {
            event.stopPropagation();
            onBookmark(lecture);
          }}
        >
          {lecture.isBookmarked ? (
            <img src={bookmark} alt="" />
          ) : (
            <i className="bi bi-bookmark" />
          )}
        </button>
      </div>
      <p className="qbl-questionCard__question">{stripHtml(lecture.title)}</p>
      <p className="qbl-questionCard__descrip">
        {stripHtml(lecture.description)}
      </p>
      {lecture.subscriptionMessage ? (
        <p className="qbl-questionCard__descrip text-danger">
          {lecture.subscriptionMessage}
        </p>
      ) : null}
      <div className="qbl-questionCard__footer">
        <button
          type="button"
          className="qbl-questionCard__footerItem"
          disabled={likePending}
          onClick={(event) => {
            event.stopPropagation();
            onLike(lecture);
          }}
        >
          <i
            className={`bi ${lecture.isLiked ? "bi-heart-fill" : "bi-heart"}`}
          />
          <span>Like</span>
          <strong>{lecture.likeCount}</strong>
        </button>
        <button
          type="button"
          className="qbl-questionCard__footerItem"
          onClick={(event) => {
            event.stopPropagation();
            onFeedback("activity", lecture);
          }}
        >
          <i className="bi bi-eye" />
          <span>View</span>
          <strong>{lecture.viewCount}</strong>
        </button>
        <button
          type="button"
          className="qbl-questionCard__footerItem"
          onClick={(event) => {
            event.stopPropagation();
            onFeedback("report", lecture);
          }}
        >
          <i className="bi bi-exclamation-circle" />
          <span>Report</span>
          <strong>{lecture.reportCount}</strong>
        </button>
        <button
          type="button"
          className="qbl-questionCard__footerItem"
          onClick={(event) => {
            event.stopPropagation();
            onFeedback("note", lecture);
          }}
        >
          <i className="bi bi-journal-text" />
          <span>Notes</span>
          <strong>{lecture.notesCount}</strong>
        </button>
      </div>
    </article>
  );
}

export default function LectureList() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: sidebarData } = useSidebarData();
  const flowState = useMemo(
    () => buildLectureFlowState(location.state, getStoredLectureFlowState()),
    [location.state],
  );
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [feedback, setFeedback] = useState({ mode: "", lecture: null });
  const stateId = resolveQuestionBankStateId({
    locationState: flowState,
    sidebarData,
  });
  const stateName =
    resolveQuestionBankStateName({ locationState: flowState, sidebarData }) ||
    "State not selected";
  const subjectId = Number(
    flowState?.selectedSubject?.id ||
      flowState?.subject_id ||
      DEFAULT_SUBJECT_ID,
  );
  const queryParams = useMemo(
    () => ({
      states_id: stateId,
      category_id: LECTURES_CATEGORY_ID,
      subject_id: subjectId,
      page,
      ...contentTopicRequest(flowState),
      ...(debouncedSearch.trim()
        ? { search_term: debouncedSearch.trim() }
        : {}),
      ...mapFilters(filters),
    }),
    [stateId, subjectId, flowState, page, debouncedSearch, filters],
  );
  const list = useLecturesList(queryParams, { enabled: Boolean(stateId) });
  const actions = useLecturesListActions(queryParams);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    persistLectureFlowState(flowState);
  }, [flowState]);
  const title =
    list.data?.topicName ||
    list.data?.subjectName ||
    list.data?.categoryName ||
    "Lectures";
  const totalPages = Number(list.data?.pagination?.totalPages || 1);
  const loading = (list.isLoading || list.isFetching) && !list.data;
  const openDetail = async (lecture) => {
    const detailFlowState = buildLectureFlowState({
      ...flowState,
      selectedLecture: lecture,
      lectures_id: lecture.id,
      lectureBackTarget: { pathname: LECTURE_ROUTES.list, state: flowState },
    });
    persistLectureFlowState(detailFlowState);
    navigate(LECTURE_ROUTES.detail, { state: detailFlowState });
  };
  return (
    <>
      <style>{`.homeDashboard-wrapper .page-content { padding-right: 0; }`}</style>
      <HomeDashboardLayout showHeader={false}>
        <div className="qbl-page qbankList-page">
          <div className="qbl-main" aria-label="Lectures content">
            <section className="qbl-panel">
              <div className="d-flex justify-content-between align-items-center d-xl-none">
                <button
                  type="button"
                  className="languageImg d-flex d-xl-none doubt-back border-0 bg-transparent p-0"
                  onClick={() => navigate(-1)}
                  aria-label="Go back"
                >
                  <BackSVG />
                </button>
                <h4 className="fw-bold mb-0 blackText">Lectures</h4>
                <div className="languageImg d-flex d-xl-none">
                  <LanguageMobileIcon />
                </div>
              </div>
              <div className="qb-body">
                <div className="d-flex align-items-center justify-content-center">
                  <div className="qb-copy">
                    <div className="qb-loc">
                      <i className="bi bi-geo-alt-fill" />
                      <span>{` ${list.data?.stateName || stateName}`}</span>
                    </div>
                    <h1 className="qb-title">{title}</h1>
                    <p className="qb-sub qbb-list">Learn more, score higher!</p>
                  </div>
                </div>
              </div>
              <div className="d-flex d-xl-block align-items-center mobile-filter-box">
                <label className="qbl-search">
                  <i className="bi bi-search" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search lectures..."
                  />
                </label>
                <button
                  type="button"
                  className="mobile-Filter-icon d-flex d-lg-flex d-xl-none"
                  onClick={() => setFilterOpen(true)}
                  aria-label="Open filters"
                >
                  <span className="filter-card__icon">
                    <i className="bi bi-sliders2" />
                  </span>
                </button>
              </div>
              {loading ? (
                <div className="qbl-questionList">
                  <ListShimmer />
                </div>
              ) : (
                <>
            <div className="qbl-summaryRow">
              <h2>{`${Number(list.data?.totalResults || 0)} Lectures Found`}</h2>
            </div>
                  <div className="qbl-questionList">
                    {list.error ? (
                      <div className="choose-state__status choose-state__status--error">
                        {list.error?.response?.data?.message ||
                          list.error?.message ||
                          "Unable to load lectures."}
                      </div>
                    ) : null}
                    {!list.error && !(list.data?.lectures || []).length ? (
                      <div className="choose-state__status">
                        No lectures available.
                      </div>
                    ) : null}
                    {(list.data?.lectures || []).map((lecture) => (
                      <LectureCard
                        key={lecture.id}
                        lecture={lecture}
                        onOpen={openDetail}
                        onBookmark={(item) =>
                          actions.bookmark.mutate({ lecture_id: item.id })
                        }
                        onLike={(item) =>
                          actions.like.mutate({ lecture_id: item.id })
                        }
                        onFeedback={(mode, item) =>
                          setFeedback({ mode, lecture: item })
                        }
                        bookmarkPending={
                          actions.bookmark.isPending &&
                          Number(actions.bookmark.variables?.lecture_id) ===
                            lecture.id
                        }
                        likePending={
                          actions.like.isPending &&
                          Number(actions.like.variables?.lecture_id) ===
                            lecture.id
                        }
                      />
                    ))}
                  </div>
                  {totalPages > 1 ? (
                    <div className="qbl-pagination">
                      <button
                        type="button"
                        className="qbl-pagination__nav"
                        disabled={page <= 1}
                        onClick={() => setPage((value) => value - 1)}
                      >
                        Previous
                      </button>
                      <span>{`${page} / ${totalPages}`}</span>
                      <button
                        type="button"
                        className="qbl-pagination__nav"
                        disabled={page >= totalPages}
                        onClick={() => setPage((value) => value + 1)}
                      >
                        Next
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          </div>
          <div
            className={`qbl-filterBackdrop ${filterOpen ? "is-open" : ""}`}
            onClick={() => setFilterOpen(false)}
          />
          <div
            id="qbank-filter-drawer"
            className={`qbl-filterPanel ${filterOpen ? "is-open" : ""}`}
          >
            <QBankFilter
              className={filterOpen ? "is-open" : ""}
              onClose={() => setFilterOpen(false)}
              queryParams={{
                states_id: stateId,
                category_id: LECTURES_CATEGORY_ID,
              }}
              appliedFilters={filters}
              onApply={(next) => {
                setFilters(next || {});
                setPage(1);
                setFilterOpen(false);
              }}
            />
          </div>
          <LectureFeedbackModal
            mode={feedback.mode}
            lecture={feedback.lecture}
            queryParams={queryParams}
            onClose={() => setFeedback({ mode: "", lecture: null })}
          />
        </div>
      </HomeDashboardLayout>
    </>
  );
}
